import { keepPreviousData } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { UseQueryResult } from "@tanstack/react-query";
import isEmpty from "lodash/isEmpty";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { fetchWorkforceProfiles } from "PFCore/services/booking/fetch_workforce_profiles";
import { CalendarRange, QueryOptions } from "PFTypes";
import { ApiFilterGroupV2, KnownFiltersV2 } from "PFTypes/filters_v2";

import { workforceKeys } from "./query_keys";
import { WorkforceResponse } from "./use_workforce_entries";

const EMPTY_RESPONSE = { entries: [], meta: { total: 0, totalPages: 0, perPage: 0, page: 0 } };
const DEFAULT_ORDER = { name: ["profile.name"], text: "A-Z", direction: "asc" };
const getWorkforceMemberFilters = (pinnedProfiles: number[]) => ({
  [ApiFilterGroupV2.Fields]: { [KnownFiltersV2.Id]: pinnedProfiles }
});

type UsePinnedWorkforceMembers = {
  pinnedIds: number[];
  dateRange: CalendarRange;
  options: QueryOptions<WorkforceResponse, { status: number }>;
};

export const usePinnedWorkforceMembers = ({
  pinnedIds,
  dateRange,
  options = {}
}: UsePinnedWorkforceMembers): UseQueryResult<WorkforceResponse, { status: number }> => {
  const filters = getWorkforceMemberFilters(pinnedIds);

  return useQuery<WorkforceResponse, { status: number }>({
    queryKey: workforceKeys.pinned(filters, DEFAULT_ORDER, dateRange, 1, PER_PAGE_UNPAGINATED),
    queryFn: () => {
      // In case workforce member filter is empty it returns all entries, but we want otherwise.
      // Setting enable=false doesn't solve the issue because then keepPreviousData remembers wrong prev data.
      if (isEmpty(pinnedIds)) {
        return Promise.resolve(EMPTY_RESPONSE as WorkforceResponse);
      }

      return fetchWorkforceProfiles({
        page: 1,
        perPage: PER_PAGE_UNPAGINATED,
        order: DEFAULT_ORDER,
        filters,
        dateRange
      });
    },
    retry: false,
    placeholderData: keepPreviousData,
    ...options
  });
};
